// 개인 근무시간 (로그인 없음) — /h/:token (예전 /my-hours/:token도 동작)
// QR로 들어온 근로자가 본인 주간 근무시간(최근 3개월)을 본다. 한국어·중국어·베트남어·크메르어.
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { buildApiUrl } from '../config/api';
import WorkHoursWeekView from '../components/WorkHoursWeekView';
import { WORK_HOURS_LANGS, WORK_HOURS_TEXT } from '../i18n/workHoursI18n';

const LANG_KEY = 'myHoursLang';

const initialLang = () => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && WORK_HOURS_TEXT[saved]) return saved;
  } catch (_) { /* 저장소 차단 */ }
  const nav = (navigator.language || '').toLowerCase();
  if (nav.startsWith('zh')) return 'zh';
  if (nav.startsWith('vi')) return 'vi';
  if (nav.startsWith('km')) return 'km';
  return 'ko';
};

const MyWorkHoursPage = () => {
  const { token } = useParams();
  const [lang, setLang] = useState(initialLang);
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = WORK_HOURS_TEXT[lang];

  // 검색 노출 방지 + 언어 표시
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.title;
    try { localStorage.setItem(LANG_KEY, lang); } catch (_) { /* 무시 */ }
    return () => { document.documentElement.lang = 'ko'; };
  }, [lang, t.title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(buildApiUrl(`/api/work-hours-public/${encodeURIComponent(token || '')}`), {
        params: date ? { date } : {},
      });
      setData(res);
      setError(null);
    } catch (e) {
      const status = e.response?.status;
      setError(status === 404 ? 'invalid' : (status === 429 ? 'tooMany' : 'error'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const week = data?.week;
  const range = data?.range;
  const canPrev = week && range && week.week_start > range.min_week;
  const canNext = week && range && week.week_start < range.max_week;
  const move = (days) => setDate(dayjs(week.week_start).add(days, 'day').format('YYYY-MM-DD'));

  return (
    <div className="my-hours">
      <header className="my-hours-top">
        <span className="my-hours-brand">WINTEC</span>
        <div className="my-hours-langs" role="group" aria-label="language">
          {WORK_HOURS_LANGS.map((l) => (
            <button key={l.code} type="button" className={lang === l.code ? 'on' : ''} onClick={() => setLang(l.code)}>
              {l.label}
            </button>
          ))}
        </div>
      </header>

      {error && <div className="my-hours-msg">{t[error]}</div>}
      {!error && loading && !data && <div className="my-hours-msg">{t.loading}</div>}

      {data && (
        <main className={loading ? 'is-loading' : ''}>
          <div className="my-hours-who">
            <h1>{data.employee.name}</h1>
            <span>{data.employee.part_name} · {t.title}</span>
          </div>

          <div className="my-hours-weeknav">
            <button type="button" disabled={!canPrev} onClick={() => move(-7)} aria-label="prev">‹</button>
            <div>
              <b>{dayjs(week.week_start).format('M.D')} – {dayjs(week.week_end).format('M.D')}</b>
              {week.week_start === range.max_week
                ? <small>{t.thisWeek}</small>
                : <small><button type="button" className="link" onClick={() => setDate(range.max_week)}>{t.thisWeek} ›</button></small>}
            </div>
            <button type="button" disabled={!canNext} onClick={() => move(7)} aria-label="next">›</button>
          </div>

          <WorkHoursWeekView
            week={week}
            trend={data.trend}
            limit={data.limit_hours}
            t={t}
            onSelectWeek={(ws) => setDate(ws)}
          />
        </main>
      )}
    </div>
  );
};

export default MyWorkHoursPage;
